import ProgressBar from '@brands/Components/ProgressBar/ProgressBar';
import closeICon from '@brands/Dashboard/Dashboard/Patient/components/closeIconSVG.svg';
import { useWindowSize } from '@brands/hooks';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { Button } from '@mui/material';
import React, { FC, useState } from 'react';
import Modal, { Styles } from 'react-modal';

import styles from './styles.module.scss';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  whatYouEntered: string;
  recommended: boolean;
  recommendedAddress: string;
  onClose: () => void;
  onConfirmChange: (selectedAddress: string) => void;
  onKeepCurrentAddress: () => void;
}

const ValidateAddressModal: FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  whatYouEntered,
  recommended,
  recommendedAddress,
  onClose,
  onConfirmChange,
  onKeepCurrentAddress,
}) => {
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const isMobile = useWindowSize().width < 992;
  React.useEffect(() => {
    if (isOpen) {
      setSelectedAddress(recommended ? recommendedAddress : whatYouEntered);
    }
  }, [isOpen, recommended, recommendedAddress, whatYouEntered]);

  const handleChange = (value: string) => {
    setSelectedAddress(value);
  };

  const handleSubmit = () => {
    if (selectedAddress === recommendedAddress) {
      onConfirmChange(selectedAddress);
    } else if (selectedAddress === whatYouEntered) {
      onKeepCurrentAddress();
    }
  };

  const mobileCustomStylesDashboard: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard?.overlay || {}),
      overflowY: 'scroll' as const,
      overflowX: 'hidden' as const,
    },
    content: {
      ...(customStylesDashboard?.content || {}),
      width: '100vw',
      height: 'calc(100vh - 64px)',
      position: 'fixed',
      top: '64px',
      transform: 'none',
      inset: '64px 0px 0px 0',
      right: '0',
      bottom: '0',
      padding: '90px 16px 32px 16px',
      margin: '0',
      borderRadius: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
  };

  const customStylesDashboardDesktop = {
    ...customStylesDashboard,
    content: {
      ...customStylesDashboard.content,
      width: '444px',
      padding: '56px 32px 32px 32px',
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={isMobile ? mobileCustomStylesDashboard : customStylesDashboardDesktop}
    >
      {isMobile ? (
        <div className={styles.backDiv}>
          <ProgressBar currentStep={1} totalSteps={1} onBack={() => onClose()} hideStepNumbers />
        </div>
      ) : (
        <div className={styles.closeICon}>
          <img src={closeICon} onClick={() => onClose()} />
        </div>
      )}
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        <div className={styles.validateAddressDiv}>
          {recommended && (
            <label className={`${styles.radioBox} ${selectedAddress === recommendedAddress ? styles.selected : ''}`}>
              <input
                type="radio"
                name="address"
                value={recommendedAddress}
                checked={selectedAddress === recommendedAddress}
                onChange={() => handleChange(recommendedAddress)}
              />
              <div className={styles.addressCardRecommended}>
                <span className={styles.addressTitle}>Verified address</span>
                <p className={styles.label}>{recommendedAddress}</p>
              </div>
            </label>
          )}

          <label className={`${styles.radioBox} ${selectedAddress === whatYouEntered ? styles.selected : ''}`}>
            <input
              type="radio"
              name="address"
              value={whatYouEntered}
              checked={selectedAddress === whatYouEntered}
              onChange={() => handleChange(whatYouEntered)}
            />
            <div className={styles.addressCard}>
              <span className={styles.addressTitle}>Entered address</span>
              <p className={styles.label}>{whatYouEntered}</p>
            </div>
          </label>
        </div>
        <div className={styles.actionBtnContainer}>
          <Button
            variant="contained"
            type="button"
            className={styles.submit}
            onClick={handleSubmit}
            disabled={!selectedAddress}
          >
            Save Changes
          </Button>
          <Button variant="text" type="button" className={styles.cancel} onClick={onClose}>
            Edit Address
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ValidateAddressModal;
